<template>
  <MediaGallery
    ref="galleryRef"
    title="Videos"
    :items="videos"
    :job-id="jobId"
    media-type="video"
    item-type-label="Video"
    card-class="photo-gallery video-gallery"
    :has-max-size-text="true"
    :gallery-options="galleryOptions"
    :get-thumbnail-url="getThumbnailUrl"
    @delete-success="(id) => emit('delete-success', id)"
  >
    <template #header-extra>
      <span class="font-sm mr-2"><b>Max size per video:</b> 500MB</span>
    </template>
    <template #upload-button>
      <SeraButton icon="fa-solid fa-video" @click="toggleFileInput">
        Upload Video(s)
        <input
          ref="fileInputRef"
          type="file"
          accept=".mp4, .mov, .avi, .webm"
          multiple
          class="hidden"
          @change="handleUpload"
        />
      </SeraButton>
    </template>
    <template #progress-modal>
      <MediaUploadProgressModal
        v-if="uploading"
        media-type="video"
        :file-count="selectedVideos.length"
        :total-file-size="totalFileSize"
        :progress="progress"
        :current-chunk="currentChunk"
        :total-chunks="totalChunks"
        @hide-modal="handleCancel"
      />
    </template>
    <template
      #viewer-modal="{
        item,
        close,
        canGoPrevious,
        canGoNext,
        goPrevious,
        goNext
      }"
    >
      <VideoPlayerModal
        v-if="item"
        :video="item"
        :can-go-previous="canGoPrevious"
        :can-go-next="canGoNext"
        :go-previous="goPrevious"
        :go-next="goNext"
        @hide-modal="close"
      />
    </template>
  </MediaGallery>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { EventBus } from "@/lib/event_bus.js";

import SeraButton from "@/components/Button.vue";
import MediaGallery from "@/components/MediaGallery.vue";
import MediaUploadProgressModal from "@/components/MediaUploadProgressModal.vue";
import CONSTANTS from "@/Constants.js";
import { Video } from "@/typings/video";
import VideoUpload, { FailedUpload } from "@/VideoGallery/VideoUpload";
import VideoPlayerModal from "./VideoPlayerModal.vue";

const { VIDEO_MAX_SIZE, MEGABYTE } = CONSTANTS;

interface VideoFile {
  file: File;
  fileName: string;
}

const MAX_VIDEO_COUNT = 5;

const props = withDefaults(
  defineProps<{
    videos: Video[];
    jobId: number | null;
  }>(),
  {
    videos: () => [],
    jobId: null
  }
);

const emit = defineEmits<{
  "delete-success": [videoId: number];
  "upload-success": [];
}>();

// Template refs
const fileInputRef = ref<HTMLInputElement | null>(null);
const galleryRef = ref<InstanceType<typeof MediaGallery> | null>(null);

// Upload state
const uploading = ref(false);
const selectedVideos = ref<VideoFile[]>([]);
const progress = ref(0);
const currentFile = ref(0);
const currentChunk = ref(0);
const totalChunks = ref(0);

let uploader: VideoUpload | null = null;

// Gallery options for useGalleryActions
const galleryOptions = {
  deleteAction: "deleteJobVideo",
  visibilityAction: "updateJobVideoVisibility",
  visibilityParamKey: "videoId",
  thumbnailEventName: "video-updated"
};

// Computed
const totalFileSize = computed(() => {
  return selectedVideos.value.reduce((total, video) => {
    return total + video.file.size;
  }, 0);
});

function getThumbnailUrl(video: Video): string {
  return video.thumbnail?.url || "";
}

// Methods
function toggleFileInput() {
  fileInputRef.value?.click();
}

function handleUpload(e: Event) {
  const target = e.target as HTMLInputElement;
  if (!target.files) return;

  if (target.files.length > MAX_VIDEO_COUNT) {
    window.notify.error(
      `You can only upload a max of ${MAX_VIDEO_COUNT} videos at one time. Try splitting videos into multiple uploads.`
    );
    return;
  }

  const data: VideoFile[] = [];
  for (let i = 0; i < target.files.length; i++) {
    const file = target.files[i];
    data.push({
      file: file,
      fileName: file.name
    });
  }
  uploadVideos(data);
}

async function uploadVideos(videos: VideoFile[]) {
  uploading.value = true;
  selectedVideos.value = videos;
  uploader = new VideoUpload({ jobId: props.jobId!, videos });
  uploader.onProgress = (progressData) => {
    progress.value = progressData.progress;
    currentFile.value = progressData.currentFile;
    currentChunk.value = progressData.currentChunk;
    totalChunks.value = progressData.totalChunks;
  };
  await uploader.uploadAll();
  handleUploadComplete();
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
}

function formatFileSize(bytes: number): string {
  return `${(bytes / MEGABYTE).toFixed(2)}MB`;
}

function getFailureReason(upload: FailedUpload): string {
  if (upload.reason === "too_large") {
    const maxSizeMB = Math.round(VIDEO_MAX_SIZE / MEGABYTE);
    return `exceeds maximum size of ${maxSizeMB}MB`;
  }
  return "failed to upload";
}

function handleUploadComplete() {
  if (!uploader) return;
  try {
    const { failures, success } = uploader;
    let message = `${success} video(s) uploaded successfully!`;
    if (failures) {
      message = `${failures} out of ${
        success + failures
      } video(s) failed to upload.<br/><br/>`;

      uploader.failedUploads.forEach((upload, i) => {
        const fileSize = formatFileSize(upload.file.size);
        const reason = getFailureReason(upload);
        message += `${i + 1}) ${
          upload.fileName
        } (${fileSize}) - ${reason}<br/>`;
      });

      window.notify.error(message);
    } else {
      window.notify.success(message);
    }
    if (document.hidden) {
      alert(message.replace(/<br\/>/gm, "\n"));
    }
  } catch {
    // no-op
  }
  resetUploader();
}

function handleCancel() {
  if (uploader) {
    uploader.onProgress = null;
    uploader.cancel();
    window.notify.error(`Cancelled uploading video(s)`);
  }
  resetUploader();
}

function resetUploader() {
  uploading.value = false;
  selectedVideos.value = [];
  progress.value = 0;
  currentFile.value = 0;
  currentChunk.value = 0;
  totalChunks.value = 0;
  uploader = null;
  emit("upload-success");
}

// Lifecycle
onMounted(() => {
  EventBus.$on("job-video-upload-complete", handleUploadComplete);
  window.onbeforeunload = (e: BeforeUnloadEvent) => {
    if (uploading.value) {
      e.preventDefault();
      return "Your pending videos will not be uploaded. Are you sure?";
    }
  };
});

onBeforeUnmount(() => {
  EventBus.$off("job-video-upload-complete", handleUploadComplete);
  window.onbeforeunload = null;
});

// Expose for testing
defineExpose({
  uploading,
  selectedVideos,
  progress,
  currentFile,
  currentChunk,
  totalChunks,
  totalFileSize,
  get deleteId() {
    return galleryRef.value?.deleteId;
  },
  get deleteConfirm() {
    return galleryRef.value?.deleteConfirm;
  },
  get viewerItem() {
    return galleryRef.value?.viewerItem;
  },
  handleDelete: (id: number) => galleryRef.value?.handleDelete(id),
  handleVisibilityToggle: (item: Video) =>
    galleryRef.value?.handleVisibilityToggle(item),
  closeDeleteConfirm: () => galleryRef.value?.closeDeleteConfirm(),
  removeVideo: async () => {
    // Handled by MediaGallery
  }
});
</script>
